/**
 * MessageThread — vertically scrolling message list with date separators
 * and auto-scroll behaviour (Plan 05-07b).
 *
 * - Groups messages by Oslo calendar date; inserts a date separator when
 *   consecutive messages cross a day boundary.
 * - Auto-scrolls to bottom only when within 120px of the bottom (preserves
 *   scroll position when reading history).
 * - role="log" aria-live="polite" aria-atomic="false" per UI-SPEC §Accessibility
 *
 * data-testid: assistant-thread
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { MessageBubble } from './MessageBubble';
import type { ToolCallSummary } from './ToolCallTrace';
import type { MemoryChipEntry } from './useAssistantStream';

export interface ThreadMessage {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ToolCallSummary[];
  filterCorrected?: boolean;
  advisoryLine?: boolean;
  /** AI-21 (#193): brikkene for notater turen lagret, foreslo eller glemte (#212: også hydrert). */
  memoryChanges?: MemoryChipEntry[];
  ts?: string;
  /**
   * Only meaningful on the trailing `streamingMessage`. Defaults to true so the
   * live bubble keeps its cursor; set false to show text that stopped arriving
   * (a failed or cancelled turn) without implying more is coming.
   */
  isStreaming?: boolean;
}

/** Format a timestamp to a Europe/Oslo calendar date string (YYYY-MM-DD) */
function toOsloDate(ts: string): string {
  try {
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Oslo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(ts));
  } catch {
    return ts.slice(0, 10);
  }
}

function DateSeparator({ label }: { label: string }) {
  return (
    <div
      data-testid="assistant-date-separator"
      className="my-4 flex items-center gap-3 text-center text-xs font-bold text-ink-2"
    >
      <div className="h-px flex-1 bg-track" />
      <span>{label}</span>
      <div className="h-px flex-1 bg-track" />
    </div>
  );
}

export function MessageThread({
  messages,
  streamingMessage,
  footer,
}: {
  messages: ThreadMessage[];
  streamingMessage: ThreadMessage | null;
  /** Fase 6: vision draft-review batch, rendered inside the scroll flow. */
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 120) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length, streamingMessage?.content]);

  // Build items with date separators
  const items: Array<{ type: 'message'; msg: ThreadMessage; index: number } | { type: 'separator'; label: string }> = [];
  let lastDate: string | null = null;

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (!msg) continue;
    if (msg.ts) {
      const msgDate = toOsloDate(msg.ts);
      if (lastDate !== null && msgDate !== lastDate) {
        // Simple label: just the date — in a full implementation this would
        // check for today/yesterday and use i18n keys (handled in 05-08)
        items.push({ type: 'separator', label: msgDate });
      }
      lastDate = msgDate;
    }
    items.push({ type: 'message', msg, index: i });
  }

  return (
    <div
      ref={ref}
      data-testid="assistant-thread"
      className="-mx-5 flex-1 overflow-y-auto overscroll-contain px-5 pb-2 md:-mx-8 md:px-8"
      role="log"
      aria-live="polite"
      aria-atomic="false"
    >
      {items.map((item, i) => {
        if (item.type === 'separator') {
          return <DateSeparator key={`sep-${i}`} label={item.label} />;
        }
        const { msg, index } = item;
        return (
          <MessageBubble
            key={index}
            role={msg.role}
            index={index}
            content={msg.content}
            filterCorrected={msg.filterCorrected}
            advisoryLine={msg.advisoryLine}
            toolCalls={msg.toolCalls}
            memoryChanges={msg.memoryChanges}
          />
        );
      })}
      {streamingMessage && (
        <MessageBubble
          role="assistant"
          index={messages.length}
          content={streamingMessage.content}
          isStreaming={streamingMessage.isStreaming ?? true}
          filterCorrected={streamingMessage.filterCorrected}
          advisoryLine={streamingMessage.advisoryLine}
          toolCalls={streamingMessage.toolCalls}
          memoryChanges={streamingMessage.memoryChanges}
        />
      )}
      {footer}
    </div>
  );
}
